// Component to manage active filters in Scheduler Dashboard
import React, { useLayoutEffect, useRef, useState } from 'react';

import {
  Bars3BottomRightIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from '@heroicons/react/16/solid';
import clsx from 'clsx';

import {
  difference,
  identity,
  map,
  pickBy,
  size,
  startCase,
  union,
} from 'lodash';

import { useShouldScroll } from '@isd/lib/helpers/hooks';
import { isSupport } from '@isd/lib/helpers/userRoles';

import { useAuthContext } from '@/context/AuthContext';
import {
  FilterField,
  schedulerFilterColumns,
  useSchedulerContext,
} from '@/context/SchedulerContext';

import SchedulerOptionButton from '@/components/Scheduler/SchedulerDays/SchedulerOptions/SchedulerOptionButton';
import {
  canSearchFilterField,
  filterOptionsBySearch,
} from '@/components/Scheduler/SchedulerDays/SchedulerOptions/schedulerFilterSearch';

export default function SchedulerFilter() {
  const { schedulerFilterOptions, activeFiltersCount } = useSchedulerContext();
  const { userDoc } = useAuthContext();
  const isSupportUser = !!userDoc && isSupport(userDoc);
  const filterColumnsContainer = useRef<HTMLElement | null>(null);
  const shouldScrollFilterColumns = useShouldScroll(filterColumnsContainer);

  // Define classnames to manage width of grid container
  // based on number of columns to show
  type PossibleColCount = 1 | 2 | 3 | 4;
  const gridWidth: Record<PossibleColCount, string> = {
    1: 'w-[16rem]',
    2: 'w-[16rem] md:w-lg',
    3: 'w-[16rem] md:w-lg lg:w-3xl',
    4: 'w-[16rem] md:w-lg lg:w-3xl xl:w-5xl',
  };
  const gridCols: Record<PossibleColCount, string> = {
    1: 'grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-2 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-3 xl:lg:grid-cols-4',
  };
  // Discard columns if there's no options to filter by
  const nonEmptyOptions = pickBy(
    schedulerFilterOptions,
    // Discard empty options or options with a single value
    (options) => options.length > 1
  );
  const nonEmptyOptionsCount = size(nonEmptyOptions);
  const colCount = Math.min(nonEmptyOptionsCount, 4);

  return (
    <SchedulerOptionButton
      noMenu
      button={<Bars3BottomRightIcon className="h-6 w-6" />}
      tooltipText="Filter Jobs"
      showButtonBadge={activeFiltersCount > 0}
      dropdownContent={
        !colCount ? (
          <div className="menu-title w-60 p-5 text-center">
            There are no available filters to apply
          </div>
        ) : (
          <div
            className={clsx(
              'scheduler-filter-dropdown',
              gridWidth[colCount as PossibleColCount]
            )}
          >
            <li
              ref={(ref) => {
                filterColumnsContainer.current = ref;
              }}
              className={clsx(
                'grid max-h-[66vh] w-full overflow-x-hidden md:max-h-[70vh]',
                gridCols[colCount as PossibleColCount],
                shouldScrollFilterColumns && 'overflow-y-scroll'
              )}
            >
              {map(nonEmptyOptions, (options, optionLabel) => (
                <SchedulerFilterColumn
                  key={optionLabel}
                  optionLabel={optionLabel}
                  options={options}
                  colWidthClass={gridWidth[1]}
                  canSearch={canSearchFilterField(optionLabel as FilterField, {
                    isSupportUser,
                  })}
                />
              ))}
            </li>
            <div className="flex justify-end">
              <ClearFiltersButton />
            </div>
          </div>
        )
      }
    />
  );
}

/**
 * Watches a filter column's scrollable options container to tell whether
 * the column's full list of options overflows it, i.e. there are more
 * options than fit without scrolling. While a search narrows the rendered
 * list the last measurement of the full list is kept, so the search box
 * doesn't vanish as the list shrinks. Also calls `onHide` when the dropdown
 * closes, since a closed dropdown isn't displayed and the container loses
 * its size.
 *
 * @param {React.RefObject<HTMLElement | null>} containerRef - The column's
 *        scrollable options container.
 * @param {Object} params
 * @param {string[]} params.options - The column's full options, re-measured
 *        whenever they change.
 * @param {boolean} params.isSearching - Whether a search is narrowing the
 *        rendered options.
 * @param {Function} params.onHide - Called when the dropdown closes.
 * @returns {boolean} True if the full list of options overflows.
 */
function useDoesFullListOverflow(
  containerRef: React.RefObject<HTMLElement | null>,
  {
    options,
    isSearching,
    onHide,
  }: { options: string[]; isSearching: boolean; onHide: () => void }
) {
  const [doesOverflow, setDoesOverflow] = useState(false);
  // Keep the latest callback without re-subscribing the observer
  const onHideRef = useRef(onHide);
  useLayoutEffect(() => {
    onHideRef.current = onHide;
  });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const onResize = () => {
      if (!container.clientHeight) {
        onHideRef.current();
        return;
      }
      // Only the full list tells whether the options fit
      if (!isSearching) {
        setDoesOverflow(container.scrollHeight > container.clientHeight);
      }
    };
    onResize();
    // Runs again when the dropdown opens or closes, or the column resizes
    const observer = new ResizeObserver(onResize);
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, options, isSearching]);

  return doesOverflow;
}

function SchedulerFilterColumn({
  optionLabel,
  options,
  colWidthClass,
  canSearch,
}: {
  optionLabel: string;
  options: string[];
  colWidthClass: string;
  canSearch: boolean;
}) {
  const { schedulerFilterValues, updateAllSchedulerFilterValues } =
    useSchedulerContext();
  const filterField = optionLabel as FilterField;
  const { label, formatOption } = schedulerFilterColumns[filterField];
  const columnLabel = label || startCase(filterField);
  const filterColumnContainer = useRef<HTMLDivElement | null>(null);
  const selectedOptions = schedulerFilterValues[filterField] || [];

  // Long columns get a search box that shows only the matching options. It
  // never changes which options are selected: selected options that don't
  // match are hidden while searching but stay selected. The search is
  // cleared when the dropdown closes.
  const [searchText, setSearchText] = useState('');
  const isSearching = searchText.trim() !== '';
  const doesFullListOverflow = useDoesFullListOverflow(filterColumnContainer, {
    options,
    isSearching,
    onHide: () => setSearchText(''),
  });
  const showSearch = canSearch && (isSearching || doesFullListOverflow);
  const visibleOptions = showSearch
    ? filterOptionsBySearch(options, searchText, { formatOption })
    : options;

  /**
   * Clears the search box on Esc, keeping focus in it.
   *
   * @param {React.KeyboardEvent<HTMLInputElement>} event - The key event.
   */
  const onKeyDownSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && searchText) {
      event.preventDefault();
      event.stopPropagation();
      setSearchText('');
    }
  };

  // Select all available values for this field or clear filter if all
  // values are already selected. While searching, only the matching options
  // are selected or cleared; other selections are left as they are.
  const onClickFieldTitle = () => {
    if (isSearching) {
      if (!visibleOptions.length) {
        return;
      }
      const areSomeShownOptionsUnselected = visibleOptions.some(
        (option) => !selectedOptions.includes(option)
      );
      updateAllSchedulerFilterValues(
        filterField,
        areSomeShownOptionsUnselected
          ? union(selectedOptions, visibleOptions)
          : difference(selectedOptions, visibleOptions)
      );
      return;
    }
    const areThereSomeOptionsUnselected =
      !selectedOptions.length || selectedOptions.length < options.length;
    updateAllSchedulerFilterValues(
      filterField,
      areThereSomeOptionsUnselected ? options : []
    );
  };

  return (
    <ul
      className={clsx('scheduler-filter-column menu', colWidthClass)}
      key={filterField}
    >
      <div className="menu-title clickable" onClick={onClickFieldTitle}>
        {columnLabel}
      </div>
      {showSearch && (
        <label className="input input-xs mb-1 w-full">
          <MagnifyingGlassIcon className="size-3 opacity-50" />
          <input
            type="search"
            className="grow"
            placeholder="Search"
            aria-label={`Search ${columnLabel} options`}
            autoComplete="off"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onKeyDown={onKeyDownSearch}
          />
        </label>
      )}
      {/* overflow-x-hidden also makes the list scroll vertically when needed */}
      <div
        ref={filterColumnContainer}
        className="max-w-full overflow-x-hidden md:max-h-64 md:flex-nowrap"
      >
        {visibleOptions.map((option) => (
          <SchedulerFilterOption
            key={option}
            filterField={filterField}
            filterValue={option}
          />
        ))}
        {!visibleOptions.length && (
          <div className="text-base-content/60 px-2 py-1 text-xs">
            No matches
          </div>
        )}
      </div>
    </ul>
  );
}

function SchedulerFilterOption({
  filterField,
  filterValue,
}: {
  filterField: FilterField;
  filterValue: string;
}) {
  const { schedulerFilterValues, updateSchedulerFilterValue } =
    useSchedulerContext();
  const formatOption =
    schedulerFilterColumns[filterField].formatOption || identity;
  return (
    <li>
      <label className="clickable px-2">
        <input
          className="checkbox-accent checkbox checkbox-xs border-base-300 rounded-sm"
          type="checkbox"
          checked={!!schedulerFilterValues[filterField]?.includes(filterValue)}
          onChange={() => updateSchedulerFilterValue(filterField, filterValue)}
        />
        <span className="label-text text-xs">{formatOption(filterValue)}</span>
      </label>
    </li>
  );
}

function ClearFiltersButton() {
  const { activeFiltersCount, clearActiveFilters } = useSchedulerContext();
  return (
    <div
      className={clsx(
        'btn btn-ghost btn-xs text-primary-dark transition-all duration-300',
        activeFiltersCount === 0 && 'btn-disabled opacity-0'
      )}
      onClick={clearActiveFilters}
    >
      <TrashIcon className="size-4" />
      Clear Filters
    </div>
  );
}
